<style scoped>
.map {
	position: absolute;
	top: 0;
	bottom: 0;
	left: 0;
	right: 0;
	height: 100%;
	width: 100%;
}

.map-style-switcher {
	position: absolute;
	top: 10px;
	right: 10px;
	z-index: 1;
	display: flex;
	border-radius: 4px;
	overflow: hidden;
	box-shadow: 0 1px 4px rgba(0, 0, 0, 0.2);
}

.style-btn {
	border: none;
	padding: 6px 12px;
	font-size: 13px;
	cursor: pointer;
	background-color: #fff;
	color: #333;
}

.style-btn:hover {
	background-color: #e9ecef;
}

.style-btn.active {
	background-color: #0d6efd;
	color: #fff;
}
</style>

<template>
	<div style="position: relative; width: 100%; height: 100%">
		<div ref="mapDiv" class="map" v-bind="$attrs"></div>
		<div class="map-style-switcher">
			<button
				class="style-btn"
				:class="{ active: activeStyle === ALIDADE_SMOOTH }"
				@click="setStyle(ALIDADE_SMOOTH)"
			>
				Map
			</button>
			<button
				class="style-btn"
				:class="{ active: activeStyle === ALIDADE_SATELLITE }"
				@click="setStyle(ALIDADE_SATELLITE)"
			>
				Satellite
			</button>
		</div>
		<slot />
	</div>
</template>

<script setup lang="ts">
import "maplibre-gl/dist/maplibre-gl.css";
import maplibregl from "maplibre-gl";
import {
	onBeforeUnmount,
	onMounted,
	provide,
	ref,
	type Ref,
	shallowRef,
	watch,
} from "vue";

import { boundsDefault, mapStyleURL } from "@/map/util";
import { ALIDADE_SMOOTH, ALIDADE_SATELLITE } from "@/map/constants";
import type { MapClickEvent, Marker } from "@/types";
import type { Location } from "@/type/api";

export type LngLatLike = maplibregl.LngLatLike;
export type LngLatBounds = maplibregl.LngLatBounds;
export interface Camera {
	zoom: number;
	north: number;
	south: number;
	east: number;
	west: number;
}
interface Emits {
	(e: "marker-drag-end", location: Location): void;
}
interface Props {
	bounds?: LngLatBounds;
	center?: LngLatLike;
	cursor?: string;
	markers?: Marker[];
	style?: string;
	zoom?: number;
}

const emit = defineEmits<Emits>();
const props = withDefaults(defineProps<Props>(), {
	bounds: boundsDefault,
	cursor: "",
	markers: () => [],
	style: ALIDADE_SMOOTH,
});

const camera = defineModel<Camera>("camera");

const mapDiv = ref<HTMLElement | null>(null);
const map: Ref<maplibregl.Map | null> = shallowRef(null);
const activeStyle = ref<string>(props.style);
const mapMarkers: Ref<Map<string, maplibregl.Marker>> = shallowRef<
	Map<string, maplibregl.Marker>
>(new Map());

// Provide the map instance to children
provide("map", map);

// Registry for tracking child components
const ons = new Map();
const onces = new Map();
const sources = new Map();
const layers = new Map();

type OnCallbackFunc = () => void;
provide(
	"registerOn",
	(
		eventname: keyof maplibregl.MapLayerEventType,
		layerid: string,
		callback: OnCallbackFunc,
	) => {
		console.log("register map.on", eventname, layerid);
		ons.set(`${eventname}.${layerid}`, {
			callback: callback,
			eventname: eventname,
			layerid: layerid,
		});
		if (map.value && map.value.loaded()) {
			map.value.on(eventname, layerid, callback);
		}
	},
);
provide(
	"registerOnce",
	(
		eventname: keyof maplibregl.MapLayerEventType,
		layerid: string,
		callback: OnCallbackFunc,
	) => {
		console.log("register map.once", eventname, layerid);
		onces.set(`${eventname}.${layerid}`, {
			callback: callback,
			eventname: eventname,
			layerid: layerid,
		});
		if (map.value && map.value.loaded()) {
			map.value.once(eventname, layerid, callback);
		}
	},
);
provide("registerSource", (id: string, config: any) => {
	console.log("register source", id, config);
	sources.set(id, config);
	if (map.value && map.value.loaded()) {
		if (!map.value.getSource(id)) {
			map.value.addSource(id, config);
		}
	}
});

provide("unregisterSource", (id: string) => {
	console.log("unregister source", id);
	/*
	sources.delete(id);
	if (map.value && map.value?.getSource(id)) {
		map.value.removeSource(id);
	}
*/
});

provide("registerLayer", (id: string, config: any) => {
	console.log("register layer", id, config);
	layers.set(id, config);
	if (map.value && map.value.loaded()) {
		if (!map.value.getLayer(id)) {
			map.value.addLayer(config);
		}
	}
});

provide("unregisterLayer", (id: string) => {
	console.log("unregister layer", id);
	/*
	layers.delete(id);
	if (map.value?.getLayer(id)) {
		map.value.removeLayer(id);
	}
*/
});

function styleURL(name: string): string {
	return mapStyleURL(name);
}

function registerCustomSourcesAndLayers(_map: maplibregl.Map): void {
	sources.forEach((config, id) => {
		console.log("adding source", id, config);
		if (!_map.getSource(id)) {
			_map.addSource(id, config);
		}
	});

	layers.forEach((config, id) => {
		console.log("adding layer", id, config);
		if (!_map.getLayer(id)) {
			_map.addLayer(config);
		}
	});

	ons.forEach((config, id) => {
		console.log("adding map.on", config.eventname, config.layerid);
		_map.on(config.eventname, config.layerid, config.callback);
	});
	onces.forEach((config, id) => {
		console.log("adding map.once", config.eventname, config.layerid);
		_map.once(config.eventname, config.layerid, config.callback);
	});
}

function setStyle(name: string): void {
	if (!map.value) return;
	activeStyle.value = name;
	map.value.setStyle(styleURL(name));
	map.value.once("styledata", () => {
		if (!map.value) return;
		registerCustomSourcesAndLayers(map.value);
	});
}

let lastEmittedCamera: Camera | null = null;

function emitCamera(): void {
	const m = map.value;
	if (!m) return;
	const b = m.getBounds();
	lastEmittedCamera = {
		zoom: m.getZoom(),
		north: b.getNorth(),
		south: b.getSouth(),
		east: b.getEast(),
		west: b.getWest(),
	};
	camera.value = lastEmittedCamera;
}

/*
watch(
	camera,
	(newCamera) => {
		if (!newCamera || !map.value) return;
		if (newCamera === lastEmittedCamera) return;
		const center: [number, number] = [
			(newCamera.east + newCamera.west) / 2,
			(newCamera.north + newCamera.south) / 2,
		];
		map.value.jumpTo({ center, zoom: newCamera.zoom });
	},
	{ deep: true },
);
*/

function initializeMap() {
	if (!mapDiv.value) return;

	console.log("initializing map...", props.bounds, props.center, props.zoom);
	const _map = new maplibregl.Map({
		bounds: props.bounds,
		container: mapDiv.value,
		center: props.center,
		style: styleURL(props.style!),
		zoom: props.zoom,
	});

	_map.on("load", () => {
		console.log("map loaded.");
		registerCustomSourcesAndLayers(_map);
		emitCamera();
	});
	_map.on("moveend", emitCamera);

	map.value = _map;
}
// Update markers on the map
const updateMarkers = () => {
	if (!map.value) return;

	// Remove markers that no longer exist
	const currentMarkerIds = new Set(props.markers.map((m) => m.id));
	for (const [id, marker] of mapMarkers.value) {
		if (!currentMarkerIds.has(id)) {
			marker.remove();
			mapMarkers.value.delete(id);
		}
	}

	// Add or update markers
	props.markers.forEach((markerData) => {
		let marker = mapMarkers.value.get(markerData.id);

		if (marker) {
			// Update existing marker
			marker.setLngLat([
				markerData.location.longitude,
				markerData.location.latitude,
			]);
			marker.setDraggable(markerData.draggable ?? false);
			// Update scale if provided
			if (markerData.scale) {
				const el = marker.getElement();
				el.style.transform =
					el.style.transform.replace(/scale\([^)]+\)/, "") +
					` scale(${markerData.scale})`;
			} else {
				const el = marker.getElement();
				el.style.transform = el.style.transform
					.replace(/scale\([^)]+\)/g, "")
					.trim();
			}
		} else {
			marker = new maplibregl.Marker({
				color: markerData.color,
				draggable: markerData.draggable ?? false,
			})
				.setLngLat([
					markerData.location.longitude,
					markerData.location.latitude,
				])
				.addTo(map.value!);

			// Handle marker drag end
			if (markerData.draggable) {
				marker.on("dragend", () => {
					const lngLat = marker!.getLngLat();
					const location: Location = {
						latitude: lngLat.lat,
						longitude: lngLat.lng,
					};
					emit("marker-drag-end", location);
				});
			}

			// Apply scale if provided
			if (markerData.scale) {
				const el = marker.getElement();
				el.style.transform = `scale(${markerData.scale})`;
			}

			mapMarkers.value.set(markerData.id, marker);
		}
	});
};

// centerOn moves the camera to a point. Exposed so a parent can drive the map
// (e.g. a "center on location" control) without owning the map instance.
function centerOn(location: Location, zoom = 16): void {
	const m = map.value;
	if (!m) return;
	if (m.loaded()) {
		m.flyTo({ center: [location.longitude, location.latitude], zoom });
	} else {
		m.once("load", () => {
			m.flyTo({ center: [location.longitude, location.latitude], zoom });
		});
	}
}
defineExpose({ centerOn });

onMounted(() => {
	initializeMap();
});

onBeforeUnmount(() => {
	if (map.value) {
		map.value.remove();
	}
});
watch(
	() => props.bounds,
	(newBounds) => {
		if (!map.value) return;
		if (map.value.loaded()) {
			map.value.fitBounds(
				newBounds,
				{ padding: 50, duration: 1000 },
				{ isInternalUpdate: true },
			);
		} else {
			map.value.once("load", () => {
				if (!map.value) return;
				map.value.fitBounds(
					newBounds,
					{ padding: 50, duration: 0 },
					{ isInternalUpdate: true },
				);
			});
		}
	},
);
watch(
	() => props.cursor,
	(newCursor) => {
		if (map.value && map.value.loaded()) {
			map.value.getCanvas().style.cursor = newCursor;
		}
	},
);
watch(
	() => props.markers,
	() => {
		updateMarkers();
	},
	{ deep: true },
);
</script>
